import React, { useState } from 'react';
import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import {
  Button,
  FluentProvider,
  webLightTheme,
  Tooltip,
  Label,
  makeStyles,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { IMilestone } from '../Interfaces';
import {
  labelPrimaryStyles,
  datePickerFocused,
  iconButtonStyles,
} from '../../../../app/common/styles/CommonStyleObjects';
import {
  CanEditDueDate,
  DueDateOffsetTooltipMessage,
  offsetDateIconStyles,
} from './milestoneDueDateUtils';
import momentUtc from '../../../../utils/DateFormatter';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';

interface IMilestoneDueDatePicker {
  index: number;
  milestone: IMilestone;
  changeDueDate: (dueDateToSave: string, milestoneId: number) => void;
  focusedMilestoneId: number;
  minMilestoneDateChange: Date;
  maxMilestoneDateChange: Date;
}

const usePickerStyles = makeStyles({
  fullWidth: { width: '100%' },
});

const MilestoneDueDatePicker: React.FunctionComponent<IMilestoneDueDatePicker> = ({
  index,
  milestone,
  changeDueDate,
  focusedMilestoneId,
  minMilestoneDateChange,
  maxMilestoneDateChange,
}: IMilestoneDueDatePicker): JSX.Element => {
  const [dueDate, setDueDate] = useState(milestone.dueDate?.toString());
  const labelPrimaryClasses = labelPrimaryStyles();
  const pickerClasses = usePickerStyles();

  const formatDate = (date: Date): Date => {
    return new Date(momentUtc(date));
  };

  const handleSelectDate = (selectedDateString: string): void => {
    changeDueDate(selectedDateString, milestone.id);
    setDueDate(selectedDateString);
  };

  const disabled = (): boolean => {
    return !CanEditDueDate(milestone);
  };

  const utcDatePickerStackItemStyle = pickerClasses.fullWidth;

  return (
    <Stack className={utcDatePickerStackItemStyle}>
      <StackItem>
        <Stack horizontal verticalAlign="center">
          <Label
            htmlFor={`milestone-${milestone.id}`}
            className={milestone.id === focusedMilestoneId ? labelPrimaryClasses.root : undefined}
          >
            {milestone.title}
          </Label>
          <Tooltip
            withArrow
            content={<span>{DueDateOffsetTooltipMessage(milestone.minimumDuration)}</span>}
            relationship="label"
          >
            <FluentProvider theme={webLightTheme}>
              <Button
                appearance="subtle"
                aria-label={`Due date information for ${milestone.title}`}
                icon={<InfoRegular color={iconButtonStyles.color} />}
                style={offsetDateIconStyles}
              />
            </FluentProvider>
          </Tooltip>
        </Stack>
      </StackItem>
      <StackItem className={utcDatePickerStackItemStyle}>
        <UtcDatePicker
          id={`milestone-${milestone.id}`}
          // Adds primary focus to milestone due date that was clicked on in milestone list
          className={milestone.id === focusedMilestoneId ? datePickerFocused : ''}
          textField={{
            name: 'dueDate',
          }}
          ariaLabel={`${milestone.title}`}
          disabled={disabled()}
          minDate={minMilestoneDateChange !== null ? formatDate(minMilestoneDateChange) : null}
          maxDate={maxMilestoneDateChange !== null ? formatDate(maxMilestoneDateChange) : null}
          value={dueDate}
          onSelectDate={handleSelectDate}
        />
      </StackItem>
    </Stack>
  );
};

export default MilestoneDueDatePicker;
